/**
 * Continuous machine voices, built once from live oscillators and the shared noise loop, then
 * only ever steered: the Osprey (twin rotors beating against each other over a turbine drone -
 * meant to be the best sound in the game, 07 §3), the mini-bunker's whine, the alarm siren, and
 * the flamethrower's roar. All on the world bus, so arctic air muffles them like anything else.
 */

import type { AircraftMix } from '../airMix';
import type { Spatial } from '../spatial';
import type { Graph } from './graph';

/** Time constant for steering a parameter: slow enough to be smooth at a 20 Hz control rate. */
const GLIDE = 0.12;

const steer = (param: AudioParam, value: number, now: number, glide = GLIDE): void => {
  param.setTargetAtTime(value, now, glide);
};

/** A voice with its own output gain and pan, silent until steered. */
class Channel {
  readonly input: GainNode;
  readonly filter: BiquadFilterNode;
  private readonly out: GainNode;
  private readonly panner: StereoPannerNode | null;

  constructor(protected readonly graph: Graph) {
    const ctx = graph.ctx;
    this.input = ctx.createGain();
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.Q.value = 0.5;
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.panner = typeof ctx.createStereoPanner === 'function' ? ctx.createStereoPanner() : null;
    this.input.connect(this.filter).connect(this.out);
    (this.panner ? this.out.connect(this.panner) : this.out).connect(graph.world);
  }

  mix(gain: number, pan: number, cutoff: number, glide = GLIDE): void {
    const now = this.graph.now;
    steer(this.out.gain, gain, now, glide);
    steer(this.filter.frequency, cutoff, now, glide);
    if (this.panner) steer(this.panner.pan, pan, now, glide);
  }

  protected osc(type: OscillatorType, hz: number): OscillatorNode {
    const o = this.graph.ctx.createOscillator();
    o.type = type;
    o.frequency.value = hz;
    o.start();
    return o;
  }

  protected tune(o: OscillatorNode | undefined, hz: number, now: number, glide = 0.5): void {
    if (o) steer(o.frequency, hz, now, glide);
  }

  protected gainNode(value: number): GainNode {
    const g = this.graph.ctx.createGain();
    g.gain.value = value;
    return g;
  }
}

const ROTOR_HZ = [19, 19.7] as const;
const TURBINE_HZ = [86, 129.5, 2140] as const;

export class OspreyVoice extends Channel {
  private readonly rotors: OscillatorNode[] = [];
  private readonly turbines: OscillatorNode[] = [];
  private readonly master = 0.85;

  constructor(graph: Graph) {
    super(graph);
    const ctx = graph.ctx;
    // Rotor wash: band-passed noise chopped by two blade-pass LFOs a fraction of a hertz apart, so
    // the "wop-wop" slowly phases the way a tiltrotor's two discs do.
    const wash = ctx.createBiquadFilter();
    wash.type = 'bandpass';
    wash.frequency.value = 190;
    wash.Q.value = 0.7;
    // A narrow low band keeps only a few per cent of the noise's power: make it up before the chop.
    const washLevel = this.gainNode(4);
    graph.noiseSource().connect(wash).connect(washLevel);
    for (const hz of ROTOR_HZ) {
      const chop = this.gainNode(0.5);
      const lfo = this.osc('sine', hz);
      lfo.connect(this.gainNode(0.5)).connect(chop.gain);
      washLevel.connect(chop).connect(this.input);
      this.rotors.push(lfo);
    }
    const levels = [0.3, 0.18, 0.02];
    TURBINE_HZ.forEach((hz, i) => {
      const o = this.osc(i < 2 ? 'sawtooth' : 'sine', hz);
      o.connect(this.gainNode(levels[i] ?? 0)).connect(this.input);
      this.turbines.push(o);
    });
  }

  update(m: AircraftMix): void {
    this.mix(m.gain * this.master, m.pan, m.cutoff, 0.25);
    const now = this.graph.now;
    for (let i = 0; i < this.rotors.length; i++) this.tune(this.rotors[i], (ROTOR_HZ[i] ?? 19) * m.rate, now);
    for (let i = 0; i < this.turbines.length; i++) this.tune(this.turbines[i], (TURBINE_HZ[i] ?? 86) * m.rate, now);
  }
}

/** The mini-bunker going live (01 §10): a servo whine that spools up, holds, and spools down. */
export class WhineVoice extends Channel {
  private readonly oscs: OscillatorNode[];

  constructor(graph: Graph) {
    super(graph);
    const band = graph.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1900;
    band.Q.value = 2.5;
    this.oscs = [this.osc('sawtooth', 600), this.osc('sawtooth', 607)];
    for (const o of this.oscs) o.connect(this.gainNode(0.2)).connect(band);
    band.connect(this.input);
  }

  /** `s` is the nearest live bunker's place in the mix, or null when none is live in earshot. */
  update(s: Spatial | null): void {
    const now = this.graph.now;
    this.mix(s ? s.gain * 0.5 : 0, s?.pan ?? 0, s?.cutoff ?? 8000, s ? 0.15 : 0.3);
    const pitch = s ? 1850 : 600;
    for (let i = 0; i < this.oscs.length; i++) this.tune(this.oscs[i], pitch + i * 9, now, s ? 0.18 : 0.25);
  }
}

/** A hand-cranked two-tone siren, rising and falling every five seconds. */
export class SirenVoice extends Channel {
  constructor(graph: Graph) {
    super(graph);
    const sweep = this.osc('sine', 0.2);
    for (const [hz, depth] of [[640, 210], [768, 250]] as const) {
      const o = this.osc('sawtooth', hz);
      sweep.connect(this.gainNode(depth)).connect(o.frequency);
      o.connect(this.gainNode(0.22)).connect(this.input);
    }
  }

  update(on: boolean): void {
    this.mix(on ? 0.5 : 0, 0, 2600, on ? 0.2 : 0.6);
  }
}

/** A flamethrower's jet: a roar with a hiss on top. */
export class FlameVoice extends Channel {
  constructor(graph: Graph) {
    super(graph);
    const ctx = graph.ctx;
    const roar = ctx.createBiquadFilter();
    roar.type = 'bandpass';
    roar.frequency.value = 420;
    roar.Q.value = 0.6;
    const hiss = ctx.createBiquadFilter();
    hiss.type = 'highpass';
    hiss.frequency.value = 3200;
    const src = graph.noiseSource();
    src.connect(roar).connect(this.gainNode(1)).connect(this.input);
    src.connect(hiss).connect(this.gainNode(0.25)).connect(this.input);
    // The jet gutters: a slow irregular wobble on the roar's pitch.
    this.osc('sine', 7.3).connect(this.gainNode(90)).connect(roar.frequency);
  }

  update(s: Spatial | null): void {
    this.mix(s ? s.gain * 0.7 : 0, s?.pan ?? 0, s?.cutoff ?? 8000, s ? 0.06 : 0.15);
  }
}
